import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output} from '@angular/core';
import {
    CatalogData,
    CatalogState,
    ChangesSavedEvent,
    HistoryEntry,
    Item,
    ItemCreatedEvent,
    ItemMovedEvent,
    ItemUpdatedEvent,
    PaneType,
    Section,
    SectionCreatedEvent,
    SectionDeletedEvent,
    SectionUpdatedEvent,
    UNSECTIONED_ITEMS_ID
} from './catalog-manager.models';
import {getImageUrl} from './catalog-manager.utils';
import {AdminService} from "../../../services/AdminService";
import {NavController} from "@ionic/angular";
import {Events} from "../../../../../framework/providers/events";

@Component({
    selector: 'app-catalog-manager',
    templateUrl: './catalog-manager.component.html',
    styleUrls: ['./catalog-manager.component.scss']
})
export class CatalogManagerComponent implements OnInit, OnChanges {
    @Input() catalogData: CatalogData = {
        items: {},
        sections: {},
        itemToSections: {}
    };

    // Internal representation
    items: Item[] = [];
    sections: Section[] = [];

    // Track deleted items and sections for save operation
    deletedItems: Set<number> = new Set();
    deletedSections: Set<number> = new Set();

    // Track original state for change detection
    private originalItems: Map<number, Item> = new Map();
    private originalSections: Map<number, Section> = new Map();

    @Output() itemCreated = new EventEmitter<ItemCreatedEvent>();
    @Output() itemUpdated = new EventEmitter<ItemUpdatedEvent>();
    @Output() itemMoved = new EventEmitter<ItemMovedEvent>();
    @Output() sectionCreated = new EventEmitter<SectionCreatedEvent>();
    @Output() sectionUpdated = new EventEmitter<SectionUpdatedEvent>();
    @Output() sectionDeleted = new EventEmitter<SectionDeletedEvent>();
    @Output() changesSaved = new EventEmitter<ChangesSavedEvent>();

    state: CatalogState = {
        activePane: 'left',
        leftPane: {selectedSectionId: null, markedItemIds: new Set(), currentRootSectionId: null, navigationVersion: 0},
        rightPane: {
            selectedSectionId: null,
            markedItemIds: new Set(),
            currentRootSectionId: null,
            navigationVersion: 0
        },
        expandedSections: new Set(),
        itemSectionLookup: new Map(),
        sectionChildren: new Map(),
        historyStack: []
    };

    selectedItem: Item | null = null;
    current_trail = [];
    last_requested_secId: number;
    current_trail_left = [];
    last_requested_secId_left: number;
    globalSearchMode: boolean = false;

    constructor(private adminSerivce: AdminService, public nav: NavController, public events: Events) {
    }

    ngOnInit() {
        this.convertInputData();
        this.normalizeData();
        this.storeOriginalState();
    }

    getImageUrl = getImageUrl;

    ngOnChanges() {
        this.convertInputData();
        this.normalizeData();

        // Update selectedItem if it's currently selected to reflect changes
        if (this.selectedItem) {
            const updatedItem = this.items.find(i => i.id === this.selectedItem!.id);
            if (updatedItem) {
                this.selectedItem = updatedItem;
            } else {
                // Item was deleted
                this.selectedItem = null;
            }
        }
    }

    private convertInputData() {
        if (!this.catalogData) {
            return;
        }
        // Convert CatalogData format to internal Item[] and Section[] arrays
        this.items = Object.keys(this.catalogData.items)
            .map(key => {
                const itemData = this.catalogData.items[key];
                // Skip removed items
                if (itemData.removed) {
                    return null;
                }
                return {
                    id: itemData.id,
                    name: itemData.name,
                    code: itemData.code,
                    images: itemData.images,
                    image: itemData.mainImageUrl || undefined,
                    section_ids: this.catalogData.itemToSections[itemData.id.toString()] || [],
                    active: itemData.active !== undefined ? itemData.active : true
                };
            })
            .filter(item => item !== null) as Item[];

        this.sections = Object.keys(this.catalogData.sections)
            .map(key => {
                const sectionData = this.catalogData.sections[key];
                // Skip removed sections
                if (sectionData.removed) {
                    return null;
                }
                return {
                    id: sectionData.id,
                    name: sectionData.name,
                    top_sec_id: sectionData.parentSectionId,
                    image: sectionData.mainImageUrl || undefined
                };
            })
            .filter(section => section !== null) as Section[];
    }

    private normalizeData() {
        // Build item -> section lookup
        this.state.itemSectionLookup.clear();
        this.items.forEach(item => {
            const sectionSet = new Set(item.section_ids);
            this.state.itemSectionLookup.set(item.id, sectionSet);
        });

        // Build section children lookup
        this.state.sectionChildren.clear();
        this.sections.forEach(section => {
            const parentId = section.top_sec_id;
            if (!this.state.sectionChildren.has(parentId)) {
                this.state.sectionChildren.set(parentId, []);
            }
            this.state.sectionChildren.get(parentId)!.push(section.id);
        });
    }

    private storeOriginalState() {
        // Store original items for change detection
        this.originalItems.clear();
        this.items.forEach(item => {
            this.originalItems.set(item.id, {...item, section_ids: [...item.section_ids]});
        });

        // Store original sections for change detection
        this.originalSections.clear();
        this.sections.forEach(section => {
            this.originalSections.set(section.id, {...section});
        });
    }

    private getItemActiveValue(item: Item): boolean {
        return item.active !== undefined ? item.active : true;
    }

    getActivePane() {
        return this.state.activePane === 'left' ? this.state.leftPane : this.state.rightPane;
    }

    getInactivePane() {
        return this.state.activePane === 'left' ? this.state.rightPane : this.state.leftPane;
    }

    getItemsForSection(sectionId: number | null): Item[] {
        // Return items that belong to the specified section
        if (sectionId === null) {
            return [];
        }
        // Special case: return items with no sections
        if (sectionId === UNSECTIONED_ITEMS_ID) {
            return this.items.filter(item => item.section_ids.length === 0);
        }
        return this.items.filter(item => item.section_ids.includes(sectionId));
    }

    // Get items for a pane - considering navigation state
    getItemsForPane(pane: PaneType, globalSearchMode:boolean): Item[] {
        if (globalSearchMode) {
            return this.items;
        }
        const paneState = pane === 'left' ? this.state.leftPane : this.state.rightPane;
        const currentRoot = paneState.currentRootSectionId;

        if (currentRoot === null) {
            // At root level, show no items
            return [];
        }

        // Special case: show unsectioned items
        if (currentRoot === UNSECTIONED_ITEMS_ID) {
            return this.items.filter(item => item.section_ids.length === 0);
        }

        // Show items only for the current section
        return this.items.filter(item => item.section_ids.includes(currentRoot));
    }

    getRootSections(): Section[] {
        return this.sections.filter(s => s.top_sec_id === null);
    }

    // Get root sections for a specific pane (respecting navigation state)
    getRootSectionsForPane(pane: PaneType): Section[] {
        const paneState = pane === 'left' ? this.state.leftPane : this.state.rightPane;
        const rootId = paneState.currentRootSectionId;

        if (rootId === null) {
            return this.sections.filter(s => s.top_sec_id === null);
        }

        return this.sections.filter(s => s.top_sec_id === rootId);
    }

    getChildSections(parentId: number | null): Section[] {
        const childIds = this.state.sectionChildren.get(parentId) || [];
        return childIds.map(id => this.sections.find(s => s.id === id)!).filter(Boolean);
    }

    isSectionExpanded(sectionId: number): boolean {
        return this.state.expandedSections.has(sectionId);
    }

    toggleSectionExpand(sectionId: number) {
        if (this.state.expandedSections.has(sectionId)) {
            this.state.expandedSections.delete(sectionId);
        } else {
            this.state.expandedSections.add(sectionId);
        }
    }

    selectSection(pane: PaneType, sectionId: number | null) {
        if (pane === 'left') {
            this.state.leftPane.selectedSectionId = sectionId;
        } else {
            this.state.rightPane.selectedSectionId = sectionId;
        }
        this.state.activePane = pane;

        // Auto-expand the selected section to show its children
        if (sectionId !== null && !this.state.expandedSections.has(sectionId)) {
            this.state.expandedSections.add(sectionId);
        }
    }

    // Navigate into a section - make it the new root
    navigateToSection(pane: PaneType, sectionId: number | null) {
        if (pane === 'left') {
            this.state.leftPane.currentRootSectionId = sectionId;
            this.state.leftPane.selectedSectionId = null;
            this.state.leftPane.navigationVersion++;
        } else {
            this.state.rightPane.currentRootSectionId = sectionId;
            this.state.rightPane.selectedSectionId = null;
            this.state.rightPane.navigationVersion++;
        }
        this.state.activePane = pane;
    }

    // Navigate up in breadcrumb
    navigateToParent(pane: PaneType, sectionId: number | null) {
        if (sectionId === null) {
            return;
        }
        // Special case: navigating from unsectioned items goes back to root
        if (sectionId === UNSECTIONED_ITEMS_ID) {
            this.navigateToSection(pane, null);
            return;
        }
        const section = this.sections.find(s => s.id === sectionId);
        if (section) {
            this.navigateToSection(pane, section.top_sec_id);
        }
    }

    toggleItemMark(itemId: number) {
        const pane = this.getActivePane();
        if (pane.markedItemIds.has(itemId)) {
            pane.markedItemIds.delete(itemId);
        } else {
            pane.markedItemIds.add(itemId);
        }
    }

    clearMarks() {
        // Clear marks in both panes and sections
        this.state.leftPane.markedItemIds.clear();
        this.state.rightPane.markedItemIds.clear();
    }

    toggleGlobalSearchMode() {
        this.globalSearchMode = !this.globalSearchMode;
        // Force item list to refresh and reset pagination when toggling in either direction
        this.state.leftPane.navigationVersion++;
        this.state.rightPane.navigationVersion++;
    }

    markAllItemsInSection(sectionId: number, pane: PaneType) {
        // Get all items in the section
        const itemsInSection = this.getItemsForSection(sectionId);

        // Get the pane state
        const paneState = pane === 'left' ? this.state.leftPane : this.state.rightPane;

        // Mark all items
        itemsInSection.forEach(item => {
            paneState.markedItemIds.add(item.id);
        });
    }

    createItem(name: string, code: string, image?: string) {
        const pane = this.getActivePane();
        // Use currentRootSectionId if available, otherwise use selectedSectionId
        const sectionId = pane.currentRootSectionId !== null ? pane.currentRootSectionId : pane.selectedSectionId;
        const newItem: Item = {
            id: Date.now(),
            name,
            code,
            image,
            section_ids: sectionId !== null ? [sectionId] : []
        };

        // Track in history
        this.addToHistory({
            type: 'item_created',
            timestamp: Date.now(),
            description: `Created item "${name}" in section ${this.getSectionName(sectionId)}`,
            undoData: {itemId: newItem.id}
        });

        this.itemCreated.emit({item: newItem});
    }

    createItemInSection(sectionId: number | null) {
        // Use negative timestamp to ensure new items appear first
        const newItem: Item = {
            id: -Date.now(),
            name: 'New Item',
            code: 'CODE',
            section_ids: sectionId !== null ? [sectionId] : []
        };

        // Track in history
        this.addToHistory({
            type: 'item_created',
            timestamp: Date.now(),
            description: `Created item "New Item" in section ${this.getSectionName(sectionId)}`,
            undoData: {itemId: newItem.id}
        });

        this.itemCreated.emit({item: newItem});
    }

    createSection(name: string) {
        const pane = this.getActivePane();
        // Use currentRootSectionId if available, otherwise use selectedSectionId
        const parentId = pane.currentRootSectionId !== null ? pane.currentRootSectionId : pane.selectedSectionId;
        const newSection: Section = {
            id: Date.now(),
            name,
            top_sec_id: parentId
        };

        // Track in history
        this.addToHistory({
            type: 'section_created',
            timestamp: Date.now(),
            description: `Created section "${name}" under ${this.getSectionName(parentId)}`,
            undoData: {sectionId: newSection.id}
        });

        this.sectionCreated.emit({section: newSection});
    }

    createSectionInSection(parentId: number | null) {
        // Use negative timestamp to ensure new sections appear first
        const newSection: Section = {
            id: -Date.now(),
            name: 'New Section',
            top_sec_id: parentId
        };

        // Track in history
        this.addToHistory({
            type: 'section_created',
            timestamp: Date.now(),
            description: `Created section "New Section" under ${this.getSectionName(parentId)}`,
            undoData: {sectionId: newSection.id}
        });

        this.sectionCreated.emit({section: newSection});
    }

    renameItem(itemId: number, newName: string) {
        const item = this.items.find(i => i.id === itemId);
        if (item) {
            const oldName = item.name;
            const updatedItem = {...item, name: newName};

            // Track in history
            this.addToHistory({
                type: 'item_updated',
                timestamp: Date.now(),
                description: `Renamed item from "${oldName}" to "${newName}"`,
                undoData: {item: {...item}}
            });

            this.itemUpdated.emit({item: updatedItem});
        }
    }

    renameSection(sectionId: number, newName: string) {
        const section = this.sections.find(s => s.id === sectionId);
        if (section) {
            const oldName = section.name;
            const updatedSection = {...section, name: newName};

            // Track in history
            this.addToHistory({
                type: 'section_updated',
                timestamp: Date.now(),
                description: `Renamed section from "${oldName}" to "${newName}"`,
                undoData: {section: {...section}}
            });

            this.sectionUpdated.emit({section: updatedSection});
        }
    }

    moveItems(itemIds: number[], fromSectionId: number | null, toSectionId: number | null, targetPane: PaneType) {
        // Defensive check: ensure itemIds is an array
        if (!Array.isArray(itemIds)) {
            console.error('moveItems expects itemIds to be an array of numbers, but received:', typeof itemIds, itemIds, '- Check drag-drop data serialization.');
            return;
        }

        // Get the target pane state
        const targetPaneState = targetPane === 'left' ? this.state.leftPane : this.state.rightPane;

        // Get the source pane state - it's the OPPOSITE of the target pane
        // Items are being dragged FROM the opposite pane TO the target pane
        const sourcePaneState = targetPane === 'left' ? this.state.rightPane : this.state.leftPane;

        // If toSectionId is null, use currentRootSectionId from target pane
        const effectiveToSectionId = toSectionId !== null ? toSectionId : targetPaneState.currentRootSectionId;

        // Determine the effective from section ID - use the source pane's currentRootSectionId
        // This is where the items are currently visible and being dragged from
        const effectiveFromSectionId = sourcePaneState.currentRootSectionId;

        console.log('moveItems called:', {
            itemIds,
            toSectionId,
            effectiveToSectionId,
            effectiveFromSectionId,
            targetPane,
            sourcePane: targetPane === 'left' ? 'right' : 'left',
            targetPaneRootId: targetPaneState.currentRootSectionId,
            sourcePaneRootId: sourcePaneState.currentRootSectionId
        });

        if (effectiveToSectionId === null) {
            alert('Please select a target section');
            return;
        }

        if (effectiveFromSectionId === null) {
            alert('Cannot move items from root level');
            return;
        }

        let movedCount = 0;
        itemIds.forEach(itemId => {
            const item = this.items.find(i => i.id === itemId);
            if (item) {
                console.log(`Processing item ${itemId}:`, {
                    currentSectionIds: item.section_ids,
                    effectiveFromSectionId,
                    effectiveToSectionId
                });

                // For move operation: Always remove from source section and add to target section
                // Even if the item exists in the target, we still need to remove it from the source

                // Track in history
                this.addToHistory({
                    type: 'item_moved',
                    timestamp: Date.now(),
                    description: `Moved item "${item.name}" from ${this.getSectionName(effectiveFromSectionId)} to ${this.getSectionName(effectiveToSectionId)}`,
                    undoData: {itemId, fromSectionId: effectiveFromSectionId, toSectionId: effectiveToSectionId}
                });

                this.itemMoved.emit({itemId, fromSectionId: effectiveFromSectionId, toSectionId: effectiveToSectionId});
                movedCount++;
                console.log(`Emitted itemMoved for item ${itemId} from ${effectiveFromSectionId} to ${effectiveToSectionId}`);
            } else {
                console.warn(`Item ${itemId} not found`);
            }
        });

        console.log(`Move operation completed. Moved ${movedCount} of ${itemIds.length} items`);

        // Clear marks after moving
        this.clearMarks();
    }

    copyItems(itemIds: number[], toSectionId: number | null, targetPane?: PaneType) {
        // Defensive check: ensure itemIds is an array
        if (!Array.isArray(itemIds)) {
            console.error('copyItems expects itemIds to be an array of numbers, but received:', typeof itemIds, itemIds, '- Check drag-drop data serialization.');
            return;
        }

        // If targetPane is provided and toSectionId is null, try to use the target pane's currentRootSectionId
        let effectiveToSectionId = toSectionId;

        if (effectiveToSectionId === null && targetPane) {
            const targetPaneState = targetPane === 'left' ? this.state.leftPane : this.state.rightPane;
            effectiveToSectionId = targetPaneState.currentRootSectionId;
        }

        if (effectiveToSectionId === null) {
            alert('Please select a target section');
            return;
        }

        // Copy is just adding the section to the item without removing the old one
        itemIds.forEach(itemId => {
            const item = this.items.find(i => i.id === itemId);
            if (item && !item.section_ids.includes(effectiveToSectionId!)) {
                const updatedItem = {
                    ...item,
                    section_ids: [...item.section_ids, effectiveToSectionId!]
                };

                // Track in history
                this.addToHistory({
                    type: 'item_updated',
                    timestamp: Date.now(),
                    description: `Copied item "${item.name}" to ${this.getSectionName(effectiveToSectionId!)}`,
                    undoData: {item: {...item}}
                });

                this.itemUpdated.emit({item: updatedItem});
            }
        });

        // Clear marks after copying
        this.clearMarks();
    }

    moveItemsBetweenSections(itemIds: number[], fromSectionId: number, toSectionId: number) {
        if (toSectionId === null) {
            alert('Please select a target section');
            return;
        }

        itemIds.forEach(itemId => {
            const item = this.items.find(i => i.id === itemId);
            if (item && item.section_ids.includes(fromSectionId)) {
                this.itemMoved.emit({itemId, fromSectionId, toSectionId});
            }
        });

        // Clear marks after moving
        this.clearMarks();
    }

    copyItemsViaDropToSection(itemIds: number[], toSectionId: number | null, targetPane: PaneType) {
        // Defensive check: ensure itemIds is an array
        if (!Array.isArray(itemIds)) {
            console.error('copyItemsViaDropToSection expects itemIds to be an array of numbers, but received:', typeof itemIds, itemIds, '- Check event binding in template.');
            return;
        }

        // Same as copyItems but called from drag-drop
        this.copyItems(itemIds, toSectionId, targetPane);
    }

    removeItemFromSection(itemId: number, sectionId: number) {
        const item = this.items.find(i => i.id === itemId);
        if (item) {
            const updatedItem = {
                ...item,
                section_ids: item.section_ids.filter(id => id !== sectionId)
            };

            // Track in history
            this.addToHistory({
                type: 'item_updated',
                timestamp: Date.now(),
                description: `Removed item "${item.name}" from section ${this.getSectionName(sectionId)}`,
                undoData: {item: {...item}}
            });

            // // Track in history
            // this.addToHistory({
            //   type: 'item_updated',
            //   timestamp: Date.now(),
            //   description: `Removed section ${this.getSectionName(sectionId)} from item "${item.name}"`,
            //   undoData: { item: { ...item } }
            // });

            // Track if item is being deleted (no more sections)
            if (updatedItem.section_ids.length === 0) {
                this.deletedItems.add(itemId);
            }

            this.itemUpdated.emit({item: updatedItem});
        }
    }

    deleteSection(sectionId: number) {
        const section = this.sections.find(s => s.id === sectionId);
        if (!section) return;

        // Get all child sections recursively
        const allDescendants = this.getAllDescendantSections(sectionId);

        // Check if user wants to delete section with children
        if (allDescendants.length > 0) {
            if (!confirm(`This section has ${allDescendants.length} subsection(s). Delete all?`)) {
                return;
            }
        }

        // Track in history
        this.addToHistory({
            type: 'section_deleted',
            timestamp: Date.now(),
            description: `Deleted section "${section.name}"${allDescendants.length > 0 ? ` and ${allDescendants.length} subsection(s)` : ''}`,
            undoData: {
                section: {...section},
                descendants: allDescendants.map(id => {
                    const desc = this.sections.find(s => s.id === id);
                    return desc ? {...desc} : null;
                }).filter(Boolean)
            }
        });

        // Track deleted sections
        this.deletedSections.add(sectionId);
        allDescendants.forEach(id => this.deletedSections.add(id));

        // Remove items from this section and all descendants (don't delete items, just remove relations)
        const sectionsToProcess = [sectionId, ...allDescendants];
        sectionsToProcess.forEach(secId => {
            const items = this.getItemsForSection(secId);
            items.forEach(item => {
                if (item.section_ids.includes(secId)) {
                    const updatedItem = {
                        ...item,
                        section_ids: item.section_ids.filter(id => id !== secId)
                    };

                    // Track if item is being deleted (no more sections)
                    // if (updatedItem.section_ids.length === 0) {
                    //     this.deletedItems.add(item.id);
                    // }
                    // this.itemUpdated.emit({item: updatedItem});
                }
            });
        });

        // Delete all descendant sections first (bottom-up)
        allDescendants.reverse().forEach(descendantId => {
            this.sectionDeleted.emit({sectionId: descendantId});
        });

        // Finally delete the section itself
        this.sectionDeleted.emit({sectionId});
    }

    // Helper method to get all descendant sections recursively
    private getAllDescendantSections(sectionId: number): number[] {
        const descendants: number[] = [];
        const children = this.getChildSections(sectionId);

        children.forEach(child => {
            descendants.push(child.id);
            // Recursively get descendants of this child
            const childDescendants = this.getAllDescendantSections(child.id);
            descendants.push(...childDescendants);
        });

        return descendants;
    }

    changeSectionParent(sectionId: number, newParentId: number | null) {
        // Validate no circular reference
        if (newParentId !== null && this.isDescendant(newParentId, sectionId)) {
            alert('Cannot create circular reference');
            return;
        }

        const section = this.sections.find(s => s.id === sectionId);
        if (section) {
            const oldParentId = section.top_sec_id;
            const updatedSection = {...section, top_sec_id: newParentId};

            // Track in history
            this.addToHistory({
                type: 'section_parent_changed',
                timestamp: Date.now(),
                description: `Moved section "${section.name}" from ${this.getSectionName(oldParentId)} to ${this.getSectionName(newParentId)}`,
                undoData: {sectionId, oldParentId, newParentId}
            });

            this.sectionUpdated.emit({section: updatedSection});
        }
    }

    private isDescendant(potentialDescendant: number, ancestor: number): boolean {
        const section = this.sections.find(s => s.id === potentialDescendant);
        if (!section) return false;
        if (section.top_sec_id === ancestor) return true;
        if (section.top_sec_id === null) return false;
        return this.isDescendant(section.top_sec_id, ancestor);
    }

    selectItem(item: Item) {
        this.selectedItem = item;
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        const pane = this.getActivePane();
        const items = this.getItemsForSection(pane.selectedSectionId);

        if (event.key === ' ') {
            event.preventDefault();
            // Mark first unmarked item or toggle last item
            if (items.length > 0) {
                this.toggleItemMark(items[0].id);
            }
        } else if (event.key === 'F2') {
            event.preventDefault();
            // Trigger rename on first marked item
        } else if (event.key === 'Delete') {
            event.preventDefault();
            // Delete marked items or selected item
        } else if (event.key === 'Tab') {
            event.preventDefault();
            this.state.activePane = this.state.activePane === 'left' ? 'right' : 'left';
        }
    }

    trackByItemId(index: number, item: Item): number {
        return item.id;
    }

    trackBySectionId(index: number, section: Section): number {
        return section.id;
    }

    getSectionPath(sectionId: number | null): string {
        if (sectionId === null) return 'All Items';
        if (sectionId === UNSECTIONED_ITEMS_ID) return 'Items Without Section';

        const path: string[] = [];
        let currentId: number | null = sectionId;

        while (currentId !== null) {
            const section = this.sections.find(s => s.id === currentId);
            if (!section) break;
            path.unshift(section.name);
            currentId = section.top_sec_id;
        }

        return path.join(' > ') || 'Unknown';
    }

    // Get breadcrumb trail as array of sections
    getBreadcrumbTrail(sectionId: number | null, left = false): Array<{ id: number | null, name: string }> {
        if (left) {
            if (this.last_requested_secId_left == sectionId) return this.current_trail_left;
            this.last_requested_secId_left = sectionId;
        } else {
            if (this.last_requested_secId == sectionId) return this.current_trail;
            this.last_requested_secId = sectionId;
        }

        const trail: Array<{ id: number | null, name: string }> = [{id: null, name: 'כל הפריטים'}];

        if (sectionId === null) return trail;

        const path: Array<{ id: number | null, name: string }> = [];
        let currentId: number | null = sectionId;

        while (currentId !== null) {
            const section = this.sections.find(s => s.id === currentId);
            if (!section) break;
            path.unshift({id: section.id, name: section.name});
            currentId = section.top_sec_id;
        }
        if (left) {
            this.current_trail_left = [...trail, ...path];
            return this.current_trail_left;
        } else {
            this.current_trail = [...trail, ...path];
            return this.current_trail;
        }
    }

    Array = Array;

    isItemMarkedInActivePane(itemId: number): boolean {
        const pane = this.getActivePane();
        return pane.markedItemIds.has(itemId);
    }

    onItemDetailsDragStart(event: DragEvent) {
        // The drag event is already set up by the item-details component
        // This handler is just for potential future use
        console.log('Item drag started from details panel');
    }

    // History management methods
    addToHistory(entry: HistoryEntry) {
        this.state.historyStack.push(entry);
    }

    getSectionName(sectionId: number | null): string {
        if (sectionId === null) return 'Root';
        if (sectionId === UNSECTIONED_ITEMS_ID) return 'Items Without Section';
        const section = this.sections.find(s => s.id === sectionId);
        return section ? section.name : 'Unknown Section';
    }

    undo() {
        if (this.state.historyStack.length === 0) {
            alert('Nothing to undo');
            return;
        }

        const lastEntry = this.state.historyStack.pop();
        if (!lastEntry) return;

        switch (lastEntry.type) {
            case 'item_created':
                // Delete the created item
                const itemToDelete = this.items.find(i => i.id === lastEntry.undoData.itemId);
                if (itemToDelete) {
                    // Track as deleted
                    this.deletedItems.add(itemToDelete.id);
                    // Remove from all sections by setting section_ids to empty
                    const updatedItem = {...itemToDelete, section_ids: []};
                    this.itemUpdated.emit({item: updatedItem});
                }
                break;

            case 'item_updated':
                // Restore the old item state
                this.itemUpdated.emit({item: lastEntry.undoData.item});
                break;

            case 'item_moved':
                // Move the item back
                this.itemMoved.emit({
                    itemId: lastEntry.undoData.itemId,
                    fromSectionId: lastEntry.undoData.toSectionId,
                    toSectionId: lastEntry.undoData.fromSectionId
                });
                break;

            case 'section_created':
                // Delete the created section
                this.sectionDeleted.emit({sectionId: lastEntry.undoData.sectionId});
                break;

            case 'section_updated':
                // Restore the old section state
                this.sectionUpdated.emit({section: lastEntry.undoData.section});
                break;

            case 'section_deleted':
                // Restore the deleted section
                this.sectionCreated.emit({section: lastEntry.undoData.section});
                // Restore descendants if any
                if (lastEntry.undoData.descendants && lastEntry.undoData.descendants.length > 0) {
                    lastEntry.undoData.descendants.forEach((desc: Section) => {
                        this.sectionCreated.emit({section: desc});
                    });
                }
                break;

            case 'section_parent_changed':
                // Restore the old parent
                const section = this.sections.find(s => s.id === lastEntry.undoData.sectionId);
                if (section) {
                    const restoredSection = {...section, top_sec_id: lastEntry.undoData.oldParentId};
                    this.sectionUpdated.emit({section: restoredSection});
                }
                break;
        }
    }

    saveChanges() {
        if (this.state.historyStack.length === 0) {
            alert('No changes to save');
            return;
        }

        // Output only changes
        const outputData: CatalogData = {
            items: {},
            sections: {},
            itemToSections: {}
        };

        // Track which items have changes
        const changedItemIds = new Set<number>();
        const changedSectionIds = new Set<number>();

        // 1. Process deleted items
        this.deletedItems.forEach(itemId => {
            const itemKey = itemId.toString();
            const originalItem = this.originalItems.get(itemId);

            outputData.items[itemKey] = {
                id: itemId,
                name: originalItem?.name || '',
                code: originalItem?.code || '',
                mainImageUrl: originalItem?.image || null,
                active: originalItem ? this.getItemActiveValue(originalItem) : true,
                removed: true,
                images: []
            };
            outputData.itemToSections[itemKey] = [];
            changedItemIds.add(itemId);
        });

        // 2. Process deleted sections
        this.deletedSections.forEach(sectionId => {
            const sectionKey = sectionId.toString();
            const originalSection = this.originalSections.get(sectionId);

            outputData.sections[sectionKey] = {
                id: sectionId,
                name: originalSection?.name || '',
                mainImageUrl: originalSection?.image || null,
                parentSectionId: originalSection?.top_sec_id || null,
                removed: true
            };
            changedSectionIds.add(sectionId);
        });

        // 3. Check for edited items (name, code, or active status changed)
        this.items.forEach(item => {
            if (changedItemIds.has(item.id)) return; // Skip already processed

            const originalItem = this.originalItems.get(item.id);

            // New item (not in original)
            if (!originalItem) {
                outputData.items[item.id.toString()] = {
                    id: item.id,
                    name: item.name,
                    code: item.code,
                    mainImageUrl: item.image || null,
                    active: this.getItemActiveValue(item),
                    images: []
                };
                changedItemIds.add(item.id);
                return;
            }

            // Check if item properties changed
            const hasNameChanged = item.name !== originalItem.name;
            const hasCodeChanged = item.code !== originalItem.code;
            const hasImageChanged = (item.image || null) !== (originalItem.image || null);
            const hasActiveChanged = this.getItemActiveValue(item) !== this.getItemActiveValue(originalItem);

            if (hasNameChanged || hasCodeChanged || hasImageChanged || hasActiveChanged) {
                outputData.items[item.id.toString()] = {
                    id: item.id,
                    name: item.name,
                    code: item.code,
                    mainImageUrl: item.image || null,
                    active: this.getItemActiveValue(item),
                    images: []
                };
                changedItemIds.add(item.id);
            }
        });

        // 4. Check for itemToSection changes (added or removed relationships)
        // First, check all current items
        this.items.forEach(item => {
            const originalItem = this.originalItems.get(item.id);
            const originalSections = originalItem?.section_ids || [];
            const currentSections = item.section_ids;

            // Check if there are differences
            const originalSet = new Set(originalSections);
            const currentSet = new Set(currentSections);

            const hasAddedSections = currentSections.some(sid => !originalSet.has(sid));
            const hasRemovedSections = originalSections.some(sid => !currentSet.has(sid));

            if (hasAddedSections || hasRemovedSections) {
                outputData.itemToSections[item.id.toString()] = [...currentSections];

                // Include item data if not already included
                // if (!changedItemIds.has(item.id)) {
                //     outputData.items[item.id.toString()] = {
                //         id: item.id,
                //         name: item.name,
                //         code: item.code,
                //         mainImageUrl: item.image || null,
                //         active: this.getItemActiveValue(item)
                //     };
                //     changedItemIds.add(item.id);
                // }
            }
        });

        // 5. Check for edited sections (name changed or parent changed)
        this.sections.forEach(section => {
            if (changedSectionIds.has(section.id)) return; // Skip already processed

            const originalSection = this.originalSections.get(section.id);

            // New section (not in original)
            if (!originalSection) {
                outputData.sections[section.id.toString()] = {
                    id: section.id,
                    name: section.name,
                    mainImageUrl: section.image || null,
                    parentSectionId: section.top_sec_id
                };
                changedSectionIds.add(section.id);
                return;
            }

            // Check if section properties changed
            const hasNameChanged = section.name !== originalSection.name;
            const hasParentChanged = section.top_sec_id !== originalSection.top_sec_id;
            const hasImageChanged = (section.image || null) !== (originalSection.image || null);

            if (hasNameChanged || hasParentChanged || hasImageChanged) {
                outputData.sections[section.id.toString()] = {
                    id: section.id,
                    name: section.name,
                    mainImageUrl: section.image || null,
                    parentSectionId: section.top_sec_id
                };
                changedSectionIds.add(section.id);
            }
        });

        // Emit the changes
        this.changesSaved.emit({data: outputData});

        // Update original state with current state for next save
        this.storeOriginalState();

        // Clear the history and deleted tracking
        this.state.historyStack = [];
        this.deletedItems.clear();
        this.deletedSections.clear();
        // alert('Changes saved successfully!');
    }

    getHistoryDescription(): string {
        if (this.state.historyStack.length === 0) return 'No changes';
        return `${this.state.historyStack.length} change(s)`;
    }

    hasHistory(): boolean {
        return this.state.historyStack.length > 0;
    }

    getTotalMarkedCount(): number {
        return this.state.leftPane.markedItemIds.size + this.state.rightPane.markedItemIds.size;
    }

    // Expose the constant for template use
    readonly UNSECTIONED_ITEMS_ID = UNSECTIONED_ITEMS_ID;
}
